import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../i18n/translations';
import {Box, Card, Typography} from '@mui/material';
import GenericHeader from '../../../../../components/generic-header/generic-header';
import {UserCaregiverForm} from '../../../../../components/user-form/user-caregiver-form';
import {useAuth} from '../../../../../contexts/auth/context';
import {useOffice} from '../../../../../contexts/office/context';
import {CaregiverMode, ScreenPaths, UserFormType} from '../../../../../enums';
import {useObserveViewport} from '../../../../../hooks';
import {RolePermissionType} from '../../../../../backend/enums/role-permission-type';
import {EntitiesRepository} from '../../../../../repositories/entities';

export default function ProfileDetailsMeOfficeScreen() {
  // Get translations
  const {t} = useTranslation();

  // Get context
  const authContext = useAuth();

  const officeContext = useOffice();

  const {isRunningMobile} = useObserveViewport();

  const userBreadcrumbName =
    officeContext.state.userForm?.user?.displayName ?? officeContext.state.userForm?.user?.name;

  // Set the user form with current user in session
  useEffect(() => {
    if (authContext.state.session?.currentUser) {
      if (
        authContext.state.session?.currentUser.id &&
        authContext.state.session?.selectedAffiliation
      ) {
        // Get user and affiliation
        EntitiesRepository.instance
          .getAffiliation(
            authContext.state.session.selectedAffiliation.entity.id,
            authContext.state.session.currentUser.id
          )
          .then((userAffiliationDetails) => {
            // Set the user form with selected affiliation
            officeContext.setUserForm(
              userAffiliationDetails.user,
              userAffiliationDetails.affiliation,
              userAffiliationDetails.affiliationQuickAccess
            );
          })
          .catch(() => {
            // Fallback
            // Set the user form
            officeContext.setUserForm(authContext.state.session!.currentUser);
          });
      } else {
        // Set the user form
        officeContext.setUserForm(authContext.state.session.currentUser);
      }

      // Log
      console.debug('🆕 User form set in office context.');
    }

    return () => {
      // Clear user
      officeContext.clearUserForm();

      // Log
      console.debug('🧹 User form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authContext.state.session?.currentUser]);

  // Redirect url
  let redirectUrl: string = ScreenPaths.Office;
  switch (officeContext.state.currentCaregiverModeEnabled) {
    case CaregiverMode.Informal:
      redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_InformalCaregiver}/${authContext.state.session?.selectedRelationship?.withUser.id}${ScreenPaths.InformalCaregiver_Activity}`;
      break;
    case CaregiverMode.Professional:
      redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authContext.state.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Activity}`;
      break;
    case CaregiverMode.SystemAdministrator:
      redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Activity}`;
      break;
    case CaregiverMode.Partner:
      redirectUrl = `${ScreenPaths.Office}${ScreenPaths.Office_Partner}${ScreenPaths.Partner_Home}`;
      break;
    default:
      redirectUrl = ScreenPaths.Office;
      break;
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
      <GenericHeader
        titleCrumbs={
          userBreadcrumbName
            ? isRunningMobile
              ? [
                  {
                    label: t(
                      translations.office.professional_caregiver.profile.generic_header.title
                    ),
                    link: `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Profile}`,
                  },
                ]
              : [
                  {
                    label: t(
                      translations.office.professional_caregiver.profile.generic_header.title
                    ),
                    link: `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Profile}`,
                  },
                  {label: userBreadcrumbName, link: '#'},
                ]
            : undefined
        }
        isLoading={officeContext.state.userFormLoadingState}
        leftColumn={
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 400,
              color: 'white',
              maxWidth: {xs: '250px', sm: '100%'},
            }}
            noWrap
          >
            {t(translations.office.professional_caregiver.profile.generic_header.subtitle.write)}
          </Typography>
        }
      />
      <Card
        sx={{
          padding: {xs: '40px 10px 51px 10px', md: '40px 66px 51px 66px'},
          minHeight: `calc(100vh - 280px)`,
          borderRadius: '10px',
        }}
        elevation={0}
      >
        <UserCaregiverForm
          userFormType={UserFormType.EditCurrentUser}
          redirectUrl={redirectUrl}
          entityId={authContext.state.session?.selectedAffiliation?.entity.id}
          canEditRole={authContext.hasWritePermission(RolePermissionType.Team_Permissions)}
        />
      </Card>
    </Box>
  );
}
